import { rgb } from 'd3-color';

/** WCAG 2.1 relative luminance for sRGB channels in 0–255. */
function relativeLuminanceSrgb255(r255: number, g255: number, b255: number): number {
  const toLinear = (c: number): number => {
    const c0 = Math.max(0, Math.min(255, c)) / 255;
    return c0 <= 0.03928 ? c0 / 12.92 : Math.pow((c0 + 0.055) / 1.055, 2.4);
  };
  const R = toLinear(r255);
  const G = toLinear(g255);
  const B = toLinear(b255);
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
}

/** True when `name` is the fleet/source label USA (case-insensitive, trimmed). */
export function isUsaSourceName(name: string | null | undefined): boolean {
  if (name == null) return false;
  return String(name).trim().toLowerCase() === 'usa';
}

/**
 * True when a CSS color string parses to a relatively dark sRGB foreground
 * (low WCAG luminance). Used to add a light halo on dark table backgrounds.
 *
 * @param threshold — luminance below this counts as “dark” (default tuned for dark UI rows).
 */
export function isChromaticForegroundDark(
  color: string | null | undefined,
  threshold = 0.55
): boolean {
  if (!color || typeof color !== 'string') return false;
  const trimmed = color.trim();
  if (!trimmed) return false;
  const c = rgb(trimmed);
  if (!Number.isFinite(c.r) || !Number.isFinite(c.g) || !Number.isFinite(c.b)) return false;
  const L = relativeLuminanceSrgb255(c.r, c.g, c.b);
  return L < threshold;
}

const HALO_CLASS = 'chromatic-cell-halo';

/**
 * Extra CSS class names (leading space) for readable chromatic text halo.
 * Applies when the label is USA or the resolved foreground color is dark.
 */
export function chromaticCellHaloClass(
  color: string | null | undefined,
  opts?: { sourceLabel?: string | null }
): string {
  const usa = isUsaSourceName(opts?.sourceLabel);
  const dark = isChromaticForegroundDark(color);
  if (usa || dark) return ` ${HALO_CLASS}`;
  return '';
}
